// src/features/monetize/components/ProductDrawer.tsx
//
// Create or edit a product. Prices are part of the create form; on an
// existing product they are rows of their own (add / archive), since a price
// a viewer already bought must keep existing.

import { useEffect } from 'react';
import {
  ActionIcon, Badge, Button, Divider, Drawer, Group, NumberInput, SegmentedControl, Select, Stack, Switch, Text, TextInput, Textarea,
} from '@mantine/core';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import { IconPlus, IconTrash } from '@tabler/icons-react';
import { useController, useFieldArray, useForm, useWatch, type Control } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { DateTimeField } from '@/shared/components/DateTimeField';
import { MONETIZE_CONSTRAINTS, PRODUCT_TYPE_DESCRIPTIONS, PRODUCT_TYPE_LABELS, PRODUCT_TYPES } from '@/lib/constants/monetize';
import type { GatingSync, MonetizeProduct, ProductType } from '@/lib/types/Monetize';
import { MonetizeUtils } from '../MonetizeUtils';
import { productFormSchema, type ProductFormValues } from '../schemas/monetizeSchemas';
import { useArchivePrice, useCreateProduct, useUpdateProduct } from '../useMonetizeMutations';
import { ScopePicker } from './ScopePicker';

interface ProductDrawerProps {
  opened: boolean;
  product: MonetizeProduct | null;
  onClose: () => void;
  onSaved: (sync: GatingSync | undefined) => void;
  onAddPrice: (product: MonetizeProduct) => void;
}

export function ProductDrawer({ opened, product, onClose, onSaved, onAddPrice }: ProductDrawerProps) {
  const isCreate = product === null;
  const { selectedCId } = useAuth();
  const createMutation = useCreateProduct();
  const updateMutation = useUpdateProduct();
  const archivePrice = useArchivePrice();
  const isPending = createMutation.isPending || updateMutation.isPending;

  const form = useForm<ProductFormValues>({
    resolver: zodResolver(productFormSchema),
    defaultValues: MonetizeUtils.defaultProductForm(),
  });

  useEffect(() => {
    if (opened) form.reset(product ? MonetizeUtils.productToForm(product) : MonetizeUtils.defaultProductForm());
  }, [opened, product, form]);

  const type = useWatch({ control: form.control, name: 'type' });

  async function handleSubmit(values: ProductFormValues) {
    if (!selectedCId) return;
    try {
      const res = isCreate
        ? await createMutation.mutateAsync(MonetizeUtils.formToProductReq(values, selectedCId, true))
        : await updateMutation.mutateAsync({ productId: product.id, req: MonetizeUtils.formToProductReq(values, selectedCId, false) });
      notifications.show({ title: isCreate ? 'Created' : 'Saved', message: `"${res.product.name}" ${isCreate ? 'is in your catalog.' : 'updated.'}`, color: 'green' });
      onSaved(res.gatingSync);
    } catch (err) {
      notifications.show({ title: 'Error', message: MonetizeUtils.errorMessage(err, `Failed to ${isCreate ? 'create' : 'save'} the product.`), color: 'red' });
    }
  }

  function confirmArchivePrice(priceId: string, label: string) {
    modals.openConfirmModal({
      title: 'Archive this price?',
      children: <Text size="sm">{label} stops being offered. Viewers who already bought it keep it.</Text>,
      labels: { confirm: 'Archive', cancel: 'Cancel' },
      confirmProps: { color: 'red' },
      onConfirm: async () => {
        try {
          await archivePrice.mutateAsync(priceId);
          notifications.show({ title: 'Archived', message: 'The price is no longer offered.', color: 'green' });
        } catch (err) {
          notifications.show({ title: 'Error', message: MonetizeUtils.errorMessage(err, 'Failed to archive the price.'), color: 'red' });
        }
      },
    });
  }

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      title={isCreate ? 'New product' : `Edit ${product.name}`}
      position="right"
      size="lg"
      padding="lg"
      closeOnClickOutside={!isPending}
      closeOnEscape={!isPending}
    >
      <form onSubmit={form.handleSubmit(handleSubmit)}>
        <Stack gap="md">
          <TypeField control={form.control} disabled={!isCreate} onTypeChange={(t) => {
            form.setValue('prices', [MonetizeUtils.defaultPriceForm(t)]);
            form.setValue('rentalWindowHours', t === 'rental' ? 48 : '');
            form.setValue('wholeService', false);
          }} />
          <Text size="xs" c="dimmed">{PRODUCT_TYPE_DESCRIPTIONS[type]}</Text>
          <NameField control={form.control} />
          <DescriptionField control={form.control} />

          <Divider label="What it includes" labelPosition="left" />
          <ScopesField control={form.control} allowWholeService={type !== 'pass'} />

          {type === 'pass' && (
            <>
              <Divider label="Event window" labelPosition="left" />
              <Group grow>
                <DateField control={form.control} name="accessStartsAt" label="Starts" description="Leave empty to start at purchase" />
                <DateField control={form.control} name="accessEndsAt" label="Ends (after replay)" description="Access stops here; leave empty for no end" />
              </Group>
            </>
          )}
          {type === 'rental' && <RentalHoursField control={form.control} />}

          <Divider label="On sale" labelPosition="left" />
          <Group grow>
            <DateField control={form.control} name="onSaleFrom" label="From" description="Empty = now" />
            <DateField control={form.control} name="onSaleUntil" label="Until" description="Empty = no end" />
          </Group>

          <ConcurrencyField control={form.control} />

          <Divider label="Prices" labelPosition="left" />
          {isCreate ? (
            <PriceRows control={form.control} type={type} />
          ) : (
            <Stack gap="xs">
              {product.prices.map((p) => (
                <Group key={p.id} justify="space-between" wrap="nowrap">
                  <Group gap="xs">
                    <Text size="sm">{MonetizeUtils.priceLabel(p)}</Text>
                    {p.nickname && <Text size="xs" c="dimmed">{p.nickname}</Text>}
                    {p.status === 'archived' && <Badge size="xs" color="gray" variant="light">Archived</Badge>}
                  </Group>
                  {p.status === 'active' && (
                    <ActionIcon variant="subtle" color="gray" aria-label="Archive price" onClick={() => confirmArchivePrice(p.id, MonetizeUtils.priceLabel(p))}>
                      <IconTrash size={16} />
                    </ActionIcon>
                  )}
                </Group>
              ))}
              <Button variant="light" size="xs" leftSection={<IconPlus size={14} />} onClick={() => onAddPrice(product)} style={{ alignSelf: 'flex-start' }}>
                Add a price
              </Button>
            </Stack>
          )}

          {isCreate && <ActiveField control={form.control} />}

          <Group justify="flex-end" mt="lg">
            <Button variant="default" onClick={onClose}>Cancel</Button>
            <Button type="submit" loading={isPending}>{isCreate ? 'Create product' : 'Save changes'}</Button>
          </Group>
        </Stack>
      </form>
    </Drawer>
  );
}

// ── Controlled fields ───────────────────────────────────────────

type C = Control<ProductFormValues>;

function TypeField({ control, disabled, onTypeChange }: { control: C; disabled: boolean; onTypeChange: (t: ProductType) => void }) {
  const { field } = useController({ control, name: 'type' });
  return (
    <SegmentedControl
      data={PRODUCT_TYPES.map((t) => ({ value: t, label: PRODUCT_TYPE_LABELS[t] }))}
      value={field.value}
      onChange={(v) => { field.onChange(v); onTypeChange(v as ProductType); }}
      disabled={disabled}
      fullWidth
    />
  );
}

function NameField({ control }: { control: C }) {
  const { field, fieldState } = useController({ control, name: 'name' });
  return (
    <TextInput
      label="Name"
      withAsterisk
      description="What viewers see on the paywall and their receipt"
      maxLength={MONETIZE_CONSTRAINTS.NAME_MAX}
      error={fieldState.error?.message}
      value={field.value}
      onChange={field.onChange}
      onBlur={field.onBlur}
    />
  );
}

function DescriptionField({ control }: { control: C }) {
  const { field, fieldState } = useController({ control, name: 'description' });
  return (
    <Textarea
      label="Description"
      autosize
      minRows={2}
      maxLength={MONETIZE_CONSTRAINTS.DESCRIPTION_MAX}
      error={fieldState.error?.message}
      value={field.value}
      onChange={field.onChange}
      onBlur={field.onBlur}
    />
  );
}

function ScopesField({ control, allowWholeService }: { control: C; allowWholeService: boolean }) {
  const scopes = useController({ control, name: 'scopes' });
  const whole = useController({ control, name: 'wholeService' });
  return (
    <ScopePicker
      value={scopes.field.value}
      onChange={scopes.field.onChange}
      wholeService={allowWholeService && whole.field.value}
      onWholeServiceChange={whole.field.onChange}
      allowWholeService={allowWholeService}
      error={scopes.fieldState.error?.message}
    />
  );
}

function DateField({ control, name, label, description }: { control: C; name: 'accessStartsAt' | 'accessEndsAt' | 'onSaleFrom' | 'onSaleUntil'; label: string; description: string }) {
  const { field, fieldState } = useController({ control, name });
  return (
    <DateTimeField
      label={label}
      description={description}
      clearable
      valueFormat="MMM D, YYYY h:mm A"
      value={field.value}
      onChange={(v) => field.onChange(v || null)}
      error={fieldState.error?.message}
    />
  );
}

function RentalHoursField({ control }: { control: C }) {
  const { field, fieldState } = useController({ control, name: 'rentalWindowHours' });
  return (
    <NumberInput
      label="Rental window"
      description="How many hours a rental plays after checkout"
      suffix=" hours"
      min={1}
      max={24 * 30}
      allowDecimal={false}
      value={field.value}
      onChange={field.onChange}
      error={fieldState.error?.message}
    />
  );
}

function ConcurrencyField({ control }: { control: C }) {
  const { field, fieldState } = useController({ control, name: 'maxConcurrentStreams' });
  return (
    <NumberInput
      label="Streams at once"
      description="How many screens one viewer may play at the same time"
      min={MONETIZE_CONSTRAINTS.MIN_CONCURRENT}
      max={MONETIZE_CONSTRAINTS.MAX_CONCURRENT}
      allowDecimal={false}
      value={field.value}
      onChange={(v) => field.onChange(typeof v === 'number' ? v : MONETIZE_CONSTRAINTS.DEFAULT_CONCURRENT)}
      error={fieldState.error?.message}
      maw={220}
    />
  );
}

function ActiveField({ control }: { control: C }) {
  const { field } = useController({ control, name: 'active' });
  return (
    <Switch
      label="Active"
      description="Active products gate their content and are offered on the paywall. Off = saved as a draft."
      checked={field.value}
      onChange={(e) => field.onChange(e.currentTarget.checked)}
    />
  );
}

function PriceRows({ control, type }: { control: C; type: ProductType }) {
  const { fields, append, remove } = useFieldArray({ control, name: 'prices' });
  const intervals = MonetizeUtils.intervalsFor(type);
  return (
    <Stack gap="sm">
      {fields.map((row, i) => (
        <Group key={row.id} align="flex-start" wrap="nowrap" gap="xs">
          <AmountField control={control} index={i} />
          <IntervalField control={control} index={i} intervals={intervals} />
          {type === 'subscription' && <TrialField control={control} index={i} />}
          <NicknameField control={control} index={i} />
          <ActionIcon variant="subtle" color="gray" mt={28} aria-label="Remove price" disabled={fields.length === 1} onClick={() => remove(i)}>
            <IconTrash size={16} />
          </ActionIcon>
        </Group>
      ))}
      {fields.length < MONETIZE_CONSTRAINTS.MAX_PRICES && (
        <Button variant="light" size="xs" leftSection={<IconPlus size={14} />} onClick={() => append(MonetizeUtils.defaultPriceForm(type))} style={{ alignSelf: 'flex-start' }}>
          Another price
        </Button>
      )}
    </Stack>
  );
}

function AmountField({ control, index }: { control: C; index: number }) {
  const { field, fieldState } = useController({ control, name: `prices.${index}.amount` as const });
  return (
    <NumberInput
      label="Amount"
      prefix="$"
      decimalScale={2}
      fixedDecimalScale
      min={0.5}
      step={1}
      value={field.value}
      onChange={field.onChange}
      error={fieldState.error?.message}
      w={130}
    />
  );
}

function IntervalField({ control, index, intervals }: { control: C; index: number; intervals: readonly string[] }) {
  const { field, fieldState } = useController({ control, name: `prices.${index}.interval` as const });
  return (
    <Select
      label="Billing"
      data={intervals.map((i) => ({ value: i, label: MonetizeUtils.intervalLabel(i as ProductFormValues['prices'][number]['interval']) }))}
      value={field.value}
      onChange={(v) => v && field.onChange(v)}
      error={fieldState.error?.message}
      allowDeselect={false}
      w={140}
    />
  );
}

function TrialField({ control, index }: { control: C; index: number }) {
  const { field, fieldState } = useController({ control, name: `prices.${index}.trialDays` as const });
  return (
    <NumberInput
      label="Free trial"
      suffix=" days"
      min={0}
      max={MONETIZE_CONSTRAINTS.MAX_TRIAL_DAYS}
      allowDecimal={false}
      value={field.value}
      onChange={(v) => field.onChange(typeof v === 'number' ? v : 0)}
      error={fieldState.error?.message}
      w={120}
    />
  );
}

function NicknameField({ control, index }: { control: C; index: number }) {
  const { field, fieldState } = useController({ control, name: `prices.${index}.nickname` as const });
  return (
    <TextInput
      label="Label"
      placeholder="Monthly"
      maxLength={MONETIZE_CONSTRAINTS.NICKNAME_MAX}
      value={field.value}
      onChange={field.onChange}
      error={fieldState.error?.message}
      style={{ flex: 1 }}
    />
  );
}
